// Onboarding restore path (docs/06 §2): re-derive the identical identity
// keypair from the recovery phrase — restoring is invisible to friends.
//
// Entry is a word-chip input built for speed: BIP39 words are uniquely
// determined by their first four letters, so typing four letters auto-commits
// the word (~4 keystrokes per word instead of spelling it out), a suggestion
// row offers prefix matches to tap, and pasting any format (spaces, line
// breaks, numbered lists) splits into chips. Words not in the wordlist become
// flagged chips so the one typo is visible instead of a generic error.
// Tapping a chip lifts the word back into the input AT ITS POSITION for
// editing (commits return to that slot); backspace on an empty input pulls
// the previous word in the same way — no tap-to-delete dead ends.

import {
  useRef,
  useState,
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import {
  PHRASE_WORDLIST,
  PHRASE_WORD_COUNT,
  isValidPhrase,
  keysFromSeed,
  normalizePhrase,
  phraseToSeed,
  type IdentityKeys,
} from '../../identity/index.js';
import { useT } from '../../i18n/index.js';

const SUGGESTION_LIMIT = 5;

export function RestorePhrase({
  onRestored,
  onBack,
}: {
  onRestored: (identity: IdentityKeys) => void;
  onBack: () => void;
}): ReactNode {
  const t = useT();
  const [words, setWords] = useState<string[]>([]);
  const [input, setInput] = useState('');
  // Slot the input currently occupies: null = after the last chip. Editing a
  // middle word moves the input (and its committed result) to that slot.
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Letters of the auto-committed word the user hasn't typed yet ("brin" →
  // committed "bring", tail "g"). Touch-typists finish the word without
  // looking; those keystrokes must be absorbed into the chip, not start a
  // phantom next word. Cleared by any non-continuation action.
  const autoTailRef = useRef('');
  /**
   * The word the last auto-commit produced, while the input is still empty. A
   * field that failed to clear (a composing mobile IME; SwiftUI and Compose hit
   * the same class of bug in the native ports) RE-DELIVERS text the reducer
   * already consumed — "dama", "damag", "damage" — and each re-delivery would
   * auto-commit again, minting a phantom chip per keystroke. Deliveries that
   * are a prefix of this word, or start with it, are echoes and are absorbed.
   * Cleared with autoTailRef; kept across pure echoes so a loop converges.
   */
  const committedRef = useRef('');
  // The word as it was when editing started, so Escape can cancel the edit
  // and put it back untouched.
  const editOriginalRef = useRef<string | undefined>(undefined);

  const inputAt = editIndex ?? words.length;
  const prefix = input.trim().toLowerCase();
  const suggestions =
    prefix.length > 0 && words.length < PHRASE_WORD_COUNT
      ? PHRASE_WORDLIST.filter((w) => w.startsWith(prefix)).slice(0, SUGGESTION_LIMIT)
      : [];

  function addWords(tokens: string[]): void {
    autoTailRef.current = '';
    committedRef.current = '';
    editOriginalRef.current = undefined;
    if (tokens.length === 0) return;
    setWords((prev) => {
      const at = editIndex ?? prev.length;
      return [...prev.slice(0, at), ...tokens, ...prev.slice(at)].slice(0, PHRASE_WORD_COUNT);
    });
    setEditIndex(null); // an edit is done once its word commits — back to the end
    setInput('');
    setError(undefined);
  }

  /** Lift the chip at `index` back into the input, in place, for editing. */
  function editWord(index: number): void {
    const word = words[index];
    if (word === undefined) return;
    autoTailRef.current = '';
    committedRef.current = '';
    // Anything half-typed in the input first commits into the input's slot,
    // so it isn't lost; account for the shift it causes when re-indexing.
    const pending = normalizePhrase(input).split(' ').filter(Boolean);
    const at = editIndex ?? words.length;
    const merged = [...words.slice(0, at), ...pending, ...words.slice(at)];
    const shifted = index + (at <= index ? pending.length : 0);
    setWords(merged.filter((_, i) => i !== shifted).slice(0, PHRASE_WORD_COUNT));
    setEditIndex(shifted);
    setInput(word);
    editOriginalRef.current = word;
    setError(undefined);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function handleChange(rawValue: string): void {
    if (
      input === '' &&
      committedRef.current !== '' &&
      rawValue !== '' &&
      !/[^a-zA-Z]/.test(rawValue)
    ) {
      const lower = rawValue.toLowerCase();
      const committed = committedRef.current;
      // Echo absorption — the native ports mirror this exactly. Two letters
      // minimum, so the first keystroke of a deliberately repeated word still
      // types. Accepted cost: the same word delivered in ONE change right
      // after its own auto-commit reads as an echo.
      if (lower.length >= 2 && committed.startsWith(lower)) {
        autoTailRef.current = committed.slice(lower.length);
        return;
      }
      if (lower.startsWith(committed)) {
        autoTailRef.current = '';
        const rest = rawValue.slice(committed.length);
        if (rest !== '') handleChange(rest);
        return;
      }
    }
    let value = rawValue;
    // Absorb keystrokes that merely finish the word the last auto-commit
    // already chipped; only what remains (if anything) starts new input.
    if (autoTailRef.current !== '' && input === '') {
      const tail = autoTailRef.current;
      const lower = value.toLowerCase();
      let k = 0;
      while (k < lower.length && k < tail.length && lower[k] === tail[k]) k++;
      autoTailRef.current = k > 0 ? tail.slice(k) : '';
      if (k === 0) committedRef.current = '';
      value = value.slice(k);
      if (value === '') {
        setInput('');
        return;
      }
    } else if (input === '') {
      autoTailRef.current = '';
      committedRef.current = '';
    }
    if (/[^a-zA-Z]/.test(value)) {
      // A separator arrived (space typed, or multi-word text): commit every
      // finished token, keep an unfinished trailing one in the input.
      const tokens = normalizePhrase(value).split(' ').filter(Boolean);
      const endsOpen = /[a-zA-Z]$/.test(value);
      if (endsOpen && tokens.length > 0) {
        addWords(tokens.slice(0, -1));
        setInput(tokens[tokens.length - 1] ?? '');
      } else {
        addWords(tokens);
      }
      return;
    }
    const lower = value.toLowerCase();
    // Auto-commit ONLY while typing forward. On deletion (backspace shrinking
    // the value) committing would instantly re-chip the word the user is
    // trying to edit — an unescapable loop (field report 2026-07-16).
    const deleting = value.length < input.length && input.toLowerCase().startsWith(lower);
    if (lower.length > 0 && !deleting) {
      // BIP39 words are unique in their first four letters: a unique prefix
      // (or an exact single match like "zoo") determines the word — commit it
      // without making the user finish spelling.
      const matches = PHRASE_WORDLIST.filter((w) => w.startsWith(lower));
      if (matches.length === 1 && matches[0] && (lower.length >= 4 || matches[0] === lower)) {
        addWords([matches[0]]);
        autoTailRef.current = matches[0].slice(lower.length);
        committedRef.current = matches[0];
        return;
      }
    }
    setInput(value);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter' && prefix.length > 0) {
      e.preventDefault();
      addWords([prefix]);
    } else if (e.key === 'Backspace' && input.length === 0 && inputAt > 0) {
      // Pull the previous word into the input for editing rather than
      // silently deleting it.
      e.preventDefault();
      editWord(inputAt - 1);
    } else if (e.key === 'Escape') {
      // Cancel: while editing, put the ORIGINAL word back in its slot
      // untouched; otherwise just clear whatever was typed.
      e.preventDefault();
      const original = editOriginalRef.current;
      if (editIndex !== null && original !== undefined) {
        addWords([original]);
      } else {
        setInput('');
        autoTailRef.current = '';
      }
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>): void {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    addWords(normalizePhrase(`${input} ${text}`).split(' ').filter(Boolean));
  }

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    // Fold any still-typed input in (at its slot), so Continue right after
    // the last word works.
    const pending = normalizePhrase(input).split(' ').filter(Boolean);
    const all = [...words.slice(0, inputAt), ...pending, ...words.slice(inputAt)].slice(
      0,
      PHRASE_WORD_COUNT,
    );
    const phrase = all.join(' ');
    if (!isValidPhrase(phrase)) {
      setWords(all);
      setInput('');
      setEditIndex(null);
      setError(t('restorePhrase.error'));
      return;
    }
    setError(undefined);
    onRestored(keysFromSeed(phraseToSeed(phrase)));
  }

  function chip(word: string, index: number): ReactNode {
    const known = PHRASE_WORDLIST.includes(word);
    // Displayed position accounts for the input's slot sitting before this
    // chip while a middle word is being edited.
    const slot = editIndex !== null && index >= editIndex ? index + 2 : index + 1;
    return (
      <button
        key={`chip-${index}-${word}`}
        type="button"
        onClick={() => editWord(index)}
        aria-label={t('restorePhrase.editWord', { n: slot })}
        className={`rounded-lg px-2 py-1 font-mono text-sm ${
          known ? 'bg-raised text-ink' : 'border border-danger text-danger'
        }`}
      >
        <span aria-hidden="true" className="mr-1 text-xs text-muted">
          {slot}
        </span>
        {word}
      </button>
    );
  }

  const showInput = words.length < PHRASE_WORD_COUNT || editIndex !== null;

  return (
    <main className="mx-auto flex min-h-full w-full max-w-app flex-col gap-6 p-6">
      <h1 className="text-2xl font-semibold text-ink">{t('restorePhrase.title')}</h1>
      <p className="text-sm text-muted">{t('restorePhrase.help')}</p>
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <label htmlFor="phrase-input" className="flex flex-col gap-1 text-sm text-ink">
          <span className="flex items-baseline justify-between">
            {t('restorePhrase.label')}
            <span data-testid="phrase-count" className="text-xs text-muted">
              {words.length} / {PHRASE_WORD_COUNT}
            </span>
          </span>
        </label>
        <div className="flex min-h-[8rem] flex-wrap content-start items-start gap-2 rounded-xl border border-edge bg-surface px-3 py-2">
          {words.slice(0, inputAt).map((word, i) => chip(word, i))}
          {showInput && (
            <input
              key="phrase-input"
              ref={inputRef}
              id="phrase-input"
              aria-label={t('restorePhrase.label')}
              type="text"
              value={input}
              onChange={(e) => handleChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={words.length === 0 ? t('restorePhrase.placeholder') : ''}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="next"
              // `flex-1` only while the input is LAST. Editing a middle word
              // puts chips after it, and stretching then leaves a hole in the
              // box and pushes the next chip against the right edge (field
              // report on iOS; this reference had the same flaw). Wedged, it
              // sizes to its content and grows as you type.
              className={
                'bg-transparent py-1 font-mono text-ink outline-none ' +
                (editIndex === null ? 'min-w-[8rem] flex-1' : 'min-w-[3.5rem]')
              }
              style={
                editIndex === null
                  ? undefined
                  : { width: `${Math.max(input.length + 1, 4)}ch` }
              }
            />
          )}
          {words.slice(inputAt).map((word, i) => chip(word, inputAt + i))}
        </div>
        {suggestions.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="phrase-suggestions">
            {suggestions.map((word) => (
              <button
                key={word}
                type="button"
                // Refocus: the tapped button unmounts with the row, and losing
                // focus to <body> closes the keyboard mid-phrase on a phone.
                onClick={() => {
                  addWords([word]);
                  inputRef.current?.focus();
                }}
                className="min-h-touch rounded-lg border border-edge bg-raised px-3 font-mono text-sm text-ink"
              >
                {word}
              </button>
            ))}
          </div>
        )}
        {error !== undefined && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            className="min-h-touch flex-1 rounded-xl border border-edge bg-surface px-4 font-medium text-ink"
          >
            {t('common.back')}
          </button>
          <button
            type="submit"
            // Enabled as soon as there is anything to submit, on every client
            // (the phrase itself is judged on submit, not by this button).
            disabled={words.length === 0 && prefix === ''}
            className="min-h-touch flex-1 rounded-xl bg-accent px-4 font-medium text-ink disabled:opacity-40 active:bg-accent-press"
          >
            {t('common.continue')}
          </button>
        </div>
      </form>
    </main>
  );
}
